import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AlertTriangle, CheckCircle2, Image } from "lucide-react";
import Button from "../../Buttons/Button";
import {
  BUTTON_CAMPAIGN_AGENT_ASK_ALTERNATIVE,
  BUTTON_CAMPAIGN_AGENT_GO_TO_PUBLISH,
  BUTTON_CAMPAIGN_AGENT_NEXT_IMPROVEMENT,
  CAMPAIGN_AGENT_ASK_ALTERNATIVE_MESSAGE,
  CAMPAIGN_AGENT_RESULT_APPLIED_DESCRIPTION,
  CAMPAIGN_AGENT_RESULT_APPLIED_TITLE,
  CAMPAIGN_AGENT_RESULT_CREATIVE_DESCRIPTION,
  CAMPAIGN_AGENT_RESULT_CREATIVE_TITLE,
  CAMPAIGN_AGENT_RESULT_FAILED_DESCRIPTION,
  CAMPAIGN_AGENT_RESULT_FAILED_TITLE,
  CAMPAIGN_AGENT_RESULT_PARTIAL_DESCRIPTION,
  CAMPAIGN_AGENT_RESULT_PARTIAL_TITLE,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useCampaignAgentPanel } from "../../../pages/NewAdsCampaign/context/CampaignAgentPanelContext";
import { highlightElementTemporarily } from "../../../pages/NewAdsCampaign/utils/fieldDomUtils";
import {
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
  LINEAR_GRADIENT_WHITE_CARD,
} from "../../../utils/colors";
import {
  CAMPAIGN_AGENT_APPLY_STATUS,
  CAMPAIGN_AGENT_HIGHLIGHT_MS,
  getCampaignAgentOptimizationElementID,
} from "../../../utils/campaignAgentOptimizations";
import { BORDER_RADIUS } from "../../../utils/constants";

const RESULT_PRESENTATION = {
  [CAMPAIGN_AGENT_APPLY_STATUS.APPLIED]: {
    Icon: CheckCircle2,
    color: "success.main",
    titleKey: CAMPAIGN_AGENT_RESULT_APPLIED_TITLE,
    descriptionKey: CAMPAIGN_AGENT_RESULT_APPLIED_DESCRIPTION,
  },
  [CAMPAIGN_AGENT_APPLY_STATUS.PARTIAL]: {
    Icon: AlertTriangle,
    color: "warning.main",
    titleKey: CAMPAIGN_AGENT_RESULT_PARTIAL_TITLE,
    descriptionKey: CAMPAIGN_AGENT_RESULT_PARTIAL_DESCRIPTION,
  },
  [CAMPAIGN_AGENT_APPLY_STATUS.FAILED]: {
    Icon: AlertTriangle,
    color: "error.main",
    titleKey: CAMPAIGN_AGENT_RESULT_FAILED_TITLE,
    descriptionKey: CAMPAIGN_AGENT_RESULT_FAILED_DESCRIPTION,
  },
  [CAMPAIGN_AGENT_APPLY_STATUS.CREATIVE]: {
    Icon: Image,
    color: "text.primary",
    titleKey: CAMPAIGN_AGENT_RESULT_CREATIVE_TITLE,
    descriptionKey: CAMPAIGN_AGENT_RESULT_CREATIVE_DESCRIPTION,
  },
};

const ChatAgentActionResult = ({
  actionResult,
  nextPendingOptimizationID = null,
  onSendAgentMessage = () => {},
  disabledInteractions = false,
}) => {
  const { t } = useTranslationApp();
  const { onFocusPublishAction } = useCampaignAgentPanel();
  const presentation = RESULT_PRESENTATION[actionResult?.status];

  if (!presentation) {
    return null;
  }

  const { Icon, color, titleKey, descriptionKey } = presentation;
  const title = actionResult?.optimizationTitle || "";
  const applied = actionResult?.appliedCount ?? 0;
  const total = actionResult?.totalCount ?? 0;
  const showPublish =
    actionResult?.status === CAMPAIGN_AGENT_APPLY_STATUS.APPLIED ||
    actionResult?.status === CAMPAIGN_AGENT_APPLY_STATUS.PARTIAL;
  const isFailed = actionResult?.status === CAMPAIGN_AGENT_APPLY_STATUS.FAILED;

  const onAskAlternative = () => {
    onSendAgentMessage(t(CAMPAIGN_AGENT_ASK_ALTERNATIVE_MESSAGE, { title }));
  };

  const onGoToNextImprovement = () => {
    highlightElementTemporarily({
      element: document.getElementById(
        getCampaignAgentOptimizationElementID(nextPendingOptimizationID)
      ),
      durationMs: CAMPAIGN_AGENT_HIGHLIGHT_MS,
      // The chat message list cancels smooth scrolling, so jump instantly.
      scrollBehavior: "auto",
    });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        boxShadow: BOX_SHADOW_THEME,
        background: LINEAR_GRADIENT_WHITE_CARD,
        borderRadius: `${BORDER_RADIUS * 4}px`,
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ color, display: "flex", flexShrink: 0, mt: 0.25 }}>
          <Icon size={18} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(descriptionKey, { title, applied, total })}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        {showPublish ? (
          <Button
            color="primary"
            disabled={disabledInteractions}
            onClick={onFocusPublishAction}
            sx={{ flex: "1 1 auto" }}
          >
            {t(BUTTON_CAMPAIGN_AGENT_GO_TO_PUBLISH)}
          </Button>
        ) : null}
        {isFailed ? (
          <Button
            color="primary"
            disabled={disabledInteractions}
            onClick={onAskAlternative}
            sx={{ flex: "1 1 auto" }}
          >
            {t(BUTTON_CAMPAIGN_AGENT_ASK_ALTERNATIVE)}
          </Button>
        ) : null}
        {nextPendingOptimizationID ? (
          <Button
            color="secondary"
            disabled={disabledInteractions}
            onClick={onGoToNextImprovement}
            sx={{ flex: "1 1 auto" }}
          >
            {t(BUTTON_CAMPAIGN_AGENT_NEXT_IMPROVEMENT)}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default ChatAgentActionResult;
